import './loanDocumentUploadPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import { ThemeProvider } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { splitFilename } from '@thesavingsgroup/utils/splitFilename';
import forEach from 'lodash/forEach';
import initial from 'lodash/initial';
import reduce from 'lodash/reduce';
import uniqueId from 'lodash/uniqueId';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { ErrorCode, FileError, FileRejection } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { InfoRoundedIcon } from '../../../components/Icons/InfoRoundedIcon';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { LoanDocumentEnum } from '../../../store/loanDocumentStore/enums/LoanDocumentEnum';
import { useLoanDocumentStoreForType } from '../../../store/loanDocumentStore/useLoanDocumentStoreForType';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';
import Dropzone from '../Dropzone';
import { LoanDocumentList } from '../shared/LoanDocumentList';
import { LoanDocumentUploadedList } from '../shared/LoanDocumentUploadedList';

type LoanDocumentUploadPageProps = {
  exampleDocumentUrl: string;
  subtitleText: string;
  titleText: string;
  type: LoanDocumentEnum;
};

export function Component({
  exampleDocumentUrl,
  subtitleText,
  titleText,
  type,
}: LoanDocumentUploadPageProps): ReactNode {
  const {
    addDocuments,
    confirm,
    hasProvidedDocuments,
    isDuplicateFile,
    loadDocuments,
    percent,
    providedDocuments,
    reset,
    uploadedDocuments,
  } = useLoanDocumentStoreForType(type);

  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { handleSubmit } = useForm();

  const [rejections, setRejections] = useState<[string, string][]>([]);
  const [removalStarted, setRemovalStarted] = useState<boolean>(false);

  const MAXIMUM_FILE_NAME_LENGTH = 12;
  const TRUNCATED_FILE_NAME_PREFIX_LENGTH = 4;
  const TRUNCATED_FILE_NAME_SUFFIX_LENGTH = 4;
  const AUTO_HIDE_DURATION = 5000;
  const ALERT_REMOVAL_DURATION = 500;

  useEffect((): (() => void) => {
    loadDocuments();

    // remove any unconfirmed files on unmount
    // this could be due to the back button, cancel button, or navigating away
    return (): void => {
      reset();
    };
  }, []);

  useEffect((): (() => void) => {
    let intervalId: NodeJS.Timeout;

    if (removalStarted) {
      intervalId = setInterval((): void => {
        if (rejections.length > 0) {
          setRejections(initial(rejections));
        } else {
          clearInterval(intervalId);
          setRemovalStarted(false);
        }
      }, ALERT_REMOVAL_DURATION);
    }

    return (): void => {
      clearInterval(intervalId);
    };
  }, [rejections, removalStarted]);

  const back = (): void => {
    navigate('/loan-documents/list');
  };

  const previousStep = (): void => {
    navigate('/loan-documents/list');
  };

  const onSubmit = (): void => {
    confirm();
    navigate('/loan-documents/list');
  };

  const getFileName = (fileName: string): string => {
    const { name, extension } = splitFilename(fileName);

    return name.length > MAXIMUM_FILE_NAME_LENGTH
      ? `${name.slice(0, TRUNCATED_FILE_NAME_PREFIX_LENGTH)}...${name.slice(
          -TRUNCATED_FILE_NAME_SUFFIX_LENGTH,
        )}.${extension}`
      : fileName;
  };

  const getErrorMessage = (error: FileError): string => {
    switch (error.code) {
      case ErrorCode.FileInvalidType:
        return t('dropzone.rejectedFileType');
      case ErrorCode.FileTooLarge:
        return t('dropzone.rejectedFileTooLarge');
      default:
        return error.message;
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]): void => {
      addDocuments(acceptedFiles);

      if (rejectedFiles.length !== 0) {
        const rejectionList = reduce(
          rejectedFiles,
          (
            list: [string, string][],
            rejectedFile: FileRejection,
          ): [string, string][] => {
            forEach(rejectedFile.errors, (error: FileError): void => {
              const fileName = getFileName(rejectedFile.file.name);
              const message = getErrorMessage(error);

              list.push([uniqueId('rejection-'), `${fileName}: ${message}`]);
            });

            return list;
          },
          [],
        );

        setRejections(rejectionList);
      }
    },
    [],
  );

  const clearRejections = (): void => {
    setRemovalStarted(true);
  };

  return (
    <div className="loan-document-upload-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          {rejections.length !== 0 && (
            <Snackbar
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              autoHideDuration={AUTO_HIDE_DURATION}
              open
              onClose={clearRejections}
            >
              <Stack flexDirection="column" gap={1}>
                {rejections.map(
                  ([key, message]): ReactNode => (
                    <Fade key={key} timeout={ALERT_REMOVAL_DURATION} in>
                      <Alert
                        data-testid={
                          locator.pages.loanDocumentUpload.alert.error
                        }
                        icon={false}
                        key={key}
                        variant="filled"
                      >
                        {message}
                      </Alert>
                    </Fade>
                  ),
                )}
              </Stack>
            </Snackbar>
          )}

          <Header
            progress={percent}
            showDrawerToggle
            onBackClick={previousStep}
          />

          <main>
            <form
              data-testid={locator.pages.loanDocumentUpload.form.root}
              noValidate
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.loanDocumentUpload.form.h1}>
                  {titleText}
                </h1>
                <h2 data-testid={locator.pages.loanDocumentUpload.form.h2}>
                  {subtitleText}
                </h2>

                <div
                  className="loan-document-example"
                  data-testid={
                    locator.pages.loanDocumentUpload.form.exampleRoot
                  }
                >
                  <span>
                    <a
                      data-testid={
                        locator.pages.loanDocumentUpload.form.exampleLink
                      }
                      href={exampleDocumentUrl}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {t('LoanDocumentUploadPage.exampleDocument')}
                    </a>
                  </span>
                  <Tooltip
                    enterTouchDelay={0}
                    leaveDelay={200}
                    title={t('LoanDocumentUploadPage.tooltip')}
                    arrow
                  >
                    <IconButton>
                      <InfoRoundedIcon className="info-icon" />
                    </IconButton>
                  </Tooltip>
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <Dropzone isDuplicate={isDuplicateFile} onDrop={onDrop} />

                  <LoanDocumentList providedDocuments={providedDocuments} />

                  {uploadedDocuments.length > 0 && (
                    <LoanDocumentUploadedList
                      uploadedDocuments={uploadedDocuments}
                    />
                  )}
                </div>

                <div className="inline-buttons">
                  <Button
                    data-testid={
                      locator.pages.loanDocumentUpload.form.cancelButton
                    }
                    variant="secondary"
                    fullWidth
                    onClick={back}
                  >
                    {t('LoanDocumentUploadPage.cancel')}
                  </Button>

                  <Button
                    data-testid={
                      locator.pages.loanDocumentUpload.form.submitButton
                    }
                    disabled={!hasProvidedDocuments}
                    fullWidth
                    submit
                  >
                    {t('LoanDocumentUploadPage.submit')}
                  </Button>
                </div>
              </div>
              <footer
                data-testid={locator.pages.loanDocumentUpload.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'LoanDocumentUploadPage';
